import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    ReactFlow,
    Background,
    Controls,
    ConnectionMode,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Node,
    ReactFlowProvider,
    useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useToast } from '@/hooks/use-toast';
import {
    createNode,
    updateNode,
    deleteNode,
    getFunnelDetails,
    validateFunnel,
    createTransition,
    deleteTransition
} from '@/services/api';
import { createButton } from '@/services/api/nodes';

import TriggerNode from './components/nodes/TriggerNode';
import MessageNode from './components/nodes/MessageNode';
import DelayNode from './components/nodes/DelayNode';
import MessageTemplateNode from './components/nodes/MessageTemplateNode';
import PhotoTemplateNode from './components/nodes/PhotoTemplateNode.tsx';
import VideoTemplateNode from './components/nodes/VideoTemplateNode';
import PhotoNode from './components/nodes/PhotoNode';
import AudioNode from './components/nodes/AudioNode';
import VideoNode from './components/nodes/VideoNode';
import TagNode from './components/nodes/TagNode';
import GoToNode from './components/nodes/GoToNode';
import ReplyNode from './components/nodes/ReplyNode';
import Sidebar from './components/Sidebar';
import Toolbar from './components/Toolbar';
import NodePanel from './components/NodePanel';
import FlowPreviewModal from './components/FlowPreviewModal';
import FunnelValidationDialog from './components/FunnelValidationDialog';
import FunnelStatsPanel from './components/FunnelStatsPanel';
import { FunnelStatsProvider, withStats } from './stats/FunnelStatsContext';
import { rangeOf, useFunnelStats, type DateRange } from '@/pages/Meta/Flow/Funnel/hooks/useFunnelStats';
import type { FunnelStatsNode } from '@/types/funnel';

import {
    FlowNodeData, NodeResponseFromAPI,
    transformNodeToFlowNode,
    transformTransitionToFlowEdge,
    TransitionResponseFromAPI
} from '@/types/flow';
import type { FlowNode, FlowEdge } from '@/types/flow';
import type { NodeCreate } from '@/types/node';
import type { FunnelValidationResponse } from '@/types/funnel';
import CustomEdge from "@/pages/Meta/Flow/Builder/components/edges/CustomEdge.tsx";
import { nodeNames, nodeConfigs } from './constants/nodeCategories';
import type { NodeType } from './constants/nodeCategories';
import { commentAutoReplyNodeCategories } from './constants/commentAutoReplyNodeCategories';
import { updateCommentAutoReply } from '@/services/api/comment-auto-replies';
import type { CommentAutoReply } from '@/services/api/comment-auto-replies';

const nodeTypes = {
    trigger: withStats(TriggerNode),
    message: withStats(MessageNode),
    message_template: withStats(MessageTemplateNode),
    photo: withStats(PhotoNode),
    photo_template: withStats(PhotoTemplateNode),
    video: withStats(VideoNode),
    video_template: withStats(VideoTemplateNode),
    audio: withStats(AudioNode),
    delay: withStats(DelayNode),
    tag: withStats(TagNode),
    go_to: withStats(GoToNode),
    reply: withStats(ReplyNode),
};

const edgeTypes = {
    custom: CustomEdge,
};


export interface FlowBuilderProps {
    builderMode?: 'flow' | 'comment_auto_reply';
    externalFunnelId?: number;
    ruleData?: CommentAutoReply;
    onBack?: () => void;
}

// Componente INTERNO (com useReactFlow)
function FlowBuilderInner({ builderMode = 'flow', externalFunnelId, ruleData, onBack }: FlowBuilderProps) {
    const params = useParams<{ funnelId: string }>();
    const funnelId = externalFunnelId ? String(externalFunnelId) : params.funnelId;
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const reactFlowInstance = useReactFlow();

    const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([]);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    // "Detalhes" no menu da lista de funis abre o builder com ?detalhes=1.
    const [searchParams, setSearchParams] = useSearchParams();
    const [showDetails, setShowDetails] = useState(
        () => builderMode !== 'comment_auto_reply' && searchParams.get('detalhes') === '1',
    );
    useEffect(() => {
        if (!searchParams.has('detalhes')) return;
        const next = new URLSearchParams(searchParams);
        next.delete('detalhes');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);
    const [statsRange, setStatsRange] = useState<DateRange>(() => rangeOf('today'));
    const [focusedNodeId, setFocusedNodeId] = useState<number | null>(null);
    const [statsPageFilter, setStatsPageFilter] = useState<string | null>(null);
    const showDetailsRef = useRef(false);
    useEffect(() => {
        showDetailsRef.current = showDetails;
        if (showDetails) setStatsRange(rangeOf('today')); // toda abertura começa em "Hoje"
        else setFocusedNodeId(null);
    }, [showDetails]);
    const [validationResult, setValidationResult] = useState<FunnelValidationResponse | null>(null);
    const [showValidation, setShowValidation] = useState(false);
    const clipboardRef = useRef<FlowNodeData | null>(null);
    const selectedNodeRef = useRef<FlowNode | null>(null);
    const nodesRef = useRef<FlowNode[]>([]);

    // Query para buscar detalhes do funil
    const { data: funnelData, isLoading } = useQuery({
        queryKey: ['funnel-details', funnelId],
        queryFn: () => getFunnelDetails(Number(funnelId)),
        enabled: !!funnelId,
    });
    // Mutation para criar node
    const createNodeMutation = useMutation({
        mutationFn: ({ data }: { data: NodeCreate }) => createNode(Number(funnelId), data),
        onSuccess: () => {
            toast({
                title: 'Node criado!',
                description: 'O node foi criado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para atualizar posição do node
    const updateNodeMutation = useMutation({
        mutationFn: ({ nodeId, data }: { nodeId: number; data: any }) => updateNode(nodeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
    });

    // Mutation para deletar node
    const deleteNodeMutation = useMutation({
        mutationFn: deleteNode,
        onSuccess: () => {
            toast({
                title: 'Node excluído!',
                description: 'O node foi excluído com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
            setSelectedNode(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation para criar transição
    const createTransitionMutation = useMutation({
        mutationFn: ({ from_node_id, to_node_id, to_button_id }: {
            from_node_id: number;
            to_node_id: number;
            to_button_id?: number | null;
        }) => createTransition({ from_node_id, to_node_id, to_button_id }),
        onSuccess: () => {
            toast({
                title: 'Conexão criada!',
                description: 'Os nodes foram conectados com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao conectar',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a conexão.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
    });

    // Mutation para deletar transição
    const deleteTransitionMutation = useMutation({
        mutationFn: deleteTransition,
        onSuccess: () => {
            toast({
                title: 'Conexão removida!',
                description: 'A conexão foi removida com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover conexão',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler para duplicar node
    const handleDuplicateNode = useCallback(async (nodeId: number) => {
        const node = nodesRef.current.find((n) => (n.data as FlowNodeData).id === nodeId);
        if (!node || !funnelId) return;

        const data = node.data as FlowNodeData;
        try {
            const newNode = await createNode(Number(funnelId), {
                node_type: data.node_type,
                node_name: `${data.node_name} (cópia)`,
                position_x: (node.position.x ?? 0) + 40,
                position_y: (node.position.y ?? 0) + 40,
                config: data.config || {},
            });

            if (Object.keys(data.config || {}).length > 0) {
                await updateNode(newNode.id, { config: data.config });
            }

            if (data.buttons?.length) {
                for (const btn of data.buttons) {
                    await createButton(newNode.id, {
                        button_text: btn.button_text,
                        button_type: btn.button_type,
                        button_value: btn.button_value ?? btn.button_text,
                        button_order: btn.position ?? btn.button_order ?? 0,
                        url: btn.url,
                    });
                }
            }

            queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
            toast({ title: 'Node duplicado!' });
        } catch (error: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao duplicar node',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        }
    }, [funnelId, queryClient, toast]);

    // Handler para editar node
    const handleEditNode = useCallback((nodeId: number) => {
        const node = nodes.find((n) => (n.data as FlowNodeData).id === nodeId);
        if (node) {
            setSelectedNode(node);
        }
    }, [nodes]);

    // Handler para deletar node
    const handleDeleteNode = useCallback(
        (nodeId: number) => {
            const node = nodesRef.current.find((n) => (n.data as FlowNodeData).id === nodeId);
            const isTrigger = (node?.data as FlowNodeData | undefined)?.node_type === 'trigger';
            const message = isTrigger
                ? 'Excluir este passo inicial? As conexões que saem dele também serão removidas.'
                : 'Tem certeza que deseja deletar este node?';
            if (confirm(message)) {
                deleteNodeMutation.mutate(nodeId);
            }
        },
        [deleteNodeMutation]
    );

    // Carregar nodes e edges quando os dados chegarem
    useEffect(() => {
        if (funnelData) {
            // Funil duplicado/importado pode ter nascido com mais de um passo
            // inicial. Nesse caso o trigger vira deletável para o usuário
            // remover o extra — o último gatilho nunca some.
            const triggerCount = funnelData.nodes.filter(
                (n) => (n as any).type_name === 'trigger'
            ).length;

            const flowNodes: FlowNode[] = funnelData.nodes.map((node) => {
                const flowNode = transformNodeToFlowNode(node as unknown as NodeResponseFromAPI);
                return {
                    ...flowNode,
                    data: {
                        ...flowNode.data,
                        canDelete: flowNode.data.node_type !== 'trigger' || triggerCount > 1,
                        tag_nome: (node as any).tag_nome,
                        tag_cor: (node as any).tag_cor,
                        onEdit: handleEditNode,
                        onDelete: handleDeleteNode,
                        onDuplicate: handleDuplicateNode,
                    },
                };
            });

            const flowEdges: FlowEdge[] = funnelData.transitions.map((transition) => {
                // Cast para o tipo esperado
                const edge = transformTransitionToFlowEdge(
                    transition as unknown as TransitionResponseFromAPI,
                    funnelData.nodes as unknown as NodeResponseFromAPI[]
                );
                return {
                    ...edge,
                    type: 'custom',
                    data: {
                        ...edge.data,
                        onDelete: (transitionId: number) => {
                            deleteTransitionMutation.mutate(transitionId);
                        },
                    },
                };
            });


            setNodes(flowNodes);
            setEdges(flowEdges);
        }
    }, [funnelData, setNodes, setEdges]);


    // Handler para conectar nodes
    const onConnect = useCallback(
        (connection: Connection) => {
            if (!connection.source || !connection.target) return;

            // ✅ NOVA LÓGICA: Detectar se a conexão vem de um botão
            let toButtonId: number | null = null;

            if (connection.sourceHandle && connection.sourceHandle.startsWith('button-')) {
                // Extrai o ID do botão do sourceHandle (ex: "button-3" -> 3)
                toButtonId = Number(connection.sourceHandle.replace('button-', ''));
            }

            setEdges((eds) => addEdge(connection, eds));

            // ✅ Enviar to_button_id quando for conexão de botão
            createTransitionMutation.mutate({
                from_node_id: Number(connection.source),
                to_node_id: Number(connection.target),
                to_button_id: toButtonId,
            });
        },
        [setEdges, createTransitionMutation]
    );


    // Handler para deletar edge
    const onEdgesDelete = useCallback(
        (edgesToDelete: FlowEdge[]) => {
            edgesToDelete.forEach((edge) => {
                if (edge.data?.id) {
                    deleteTransitionMutation.mutate(edge.data.id);
                }
            });
        },
        [deleteTransitionMutation]
    );

    // Handler para quando o node para de ser arrastado
    const onNodeDragStop = useCallback(
        (_event: any, node: Node) => {
            updateNodeMutation.mutate({
                nodeId: Number(node.id),
                data: {
                    position_x: node.position.x,
                    position_y: node.position.y,
                },
            });
        },
        [updateNodeMutation]
    );

    // Handler para selecionar node
    const onNodeClick = useCallback((_event: React.MouseEvent, node: FlowNode) => {
        // Com o painel de desempenho aberto, clicar no node mostra os números dele em vez de abrir o editor.
        if (showDetailsRef.current) {
            focusNodeRef.current(node.data.id);
            return;
        }
        setSelectedNode(node);
        selectedNodeRef.current = node;
    }, []);

    const funnelStats = useFunnelStats(funnelId ? Number(funnelId) : null, statsRange, showDetails, statsPageFilter);
    const statsByNode = useMemo(
        () => new Map<number, FunnelStatsNode>((funnelStats.details?.nodes ?? []).map((n) => [n.node_id, n])),
        [funnelStats.details],
    );

    // O cartão do node abre à direita dele (400px + folga): centraliza o
    // conjunto node + cartão, não só o node, para os dois caberem na tela.
    const focusNode = useCallback((nodeId: number) => {
        setFocusedNodeId(nodeId);
        const n = reactFlowInstance.getNode(String(nodeId));
        if (!n) return;
        const zoom = Math.max(reactFlowInstance.getZoom(), 0.9);
        const w = n.measured?.width ?? 280;
        const cardW = 420 / zoom;
        const paneH = document.querySelector('.react-flow')?.getBoundingClientRect().height ?? 800;
        // Topo do node a ~90px do topo do canvas: o cartão tem até 72vh de altura.
        reactFlowInstance.setCenter(n.position.x + (w + cardW) / 2, n.position.y + (paneH / 2 - 90) / zoom, {
            zoom,
            duration: 600,
        });
    }, [reactFlowInstance]);
    const focusNodeRef = useRef(focusNode);
    focusNodeRef.current = focusNode;

    // Handler para adicionar novo node NO CENTRO da viewport
    const handleAddNode = useCallback(
        (nodeType: NodeType) => {
            try {
                // Pega o container visível do ReactFlow
                const wrapper = document.querySelector('.react-flow');
                if (!wrapper) return;

                const rect = wrapper.getBoundingClientRect();
                // Converte o centro da tela visível para coordenadas do flow
                const center = reactFlowInstance.screenToFlowPosition({
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2,
                });

                const newNodeData: NodeCreate = {
                    node_type: nodeType,
                    node_name: nodeNames[nodeType] || 'Novo Node',
                    position_x: Math.round(center.x),
                    position_y: Math.round(center.y),
                    config: nodeConfigs[nodeType] || {},
                };

                createNodeMutation.mutate({ data: newNodeData });
            } catch (error) {
                console.error('Erro ao criar node:', error);
                const newNodeData: NodeCreate = {
                    node_type: nodeType,
                    node_name: nodeNames[nodeType] || 'Novo Node',
                    position_x: 100,
                    position_y: 100,
                    config: nodeConfigs[nodeType] || {},
                };
                createNodeMutation.mutate({ data: newNodeData });
            }
        },
        [createNodeMutation, reactFlowInstance]
    );

    // Handler para validar funil
    const handleValidate = useCallback(async () => {
        try {
            const result = await validateFunnel(Number(funnelId));
            if (result.valid) {
                setShowValidation(false);
                toast({
                    title: 'Funil válido!',
                    description: 'O funil está pronto para ser ativado.',
                });
            } else {
                setValidationResult(result);
                setShowValidation(true);
            }
        } catch (error: any) {
            toast({
                variant: 'destructive',
                title: 'Erro ao validar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        }
    }, [funnelId, toast]);

    // Keyboard shortcuts: Ctrl+C (copiar), Ctrl+V (colar)
    useEffect(() => {
        const handleKeyDown = async (e: KeyboardEvent) => {
            const isCtrlOrCmd = e.ctrlKey || e.metaKey;
            if (!isCtrlOrCmd) return;
            if (e.key !== 'c' && e.key !== 'v') return;

            // Ignora se estiver digitando em input/textarea
            const tag = (e.target as HTMLElement).tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if ((e.target as HTMLElement).isContentEditable) return;

            // Ctrl+C — copiar node selecionado
            if (e.key === 'c') {
                const node = selectedNodeRef.current;
                if (!node) return;
                const data = node.data as FlowNodeData;
                if (data.node_type === 'trigger') return;
                e.preventDefault();
                clipboardRef.current = data;
                toast({ title: 'Node copiado!' });
            }

            // Ctrl+V — colar node
            if (e.key === 'v') {
                const copiedNode = clipboardRef.current;
                if (!copiedNode || !funnelId) return;
                e.preventDefault();

                try {
                    // Posicao no centro do viewport
                    const { x, y, zoom } = reactFlowInstance.getViewport();
                    const container = document.querySelector('.react-flow__viewport');
                    const bounds = container?.getBoundingClientRect();
                    const centerX = bounds ? Math.round((bounds.width / 2 - x) / zoom) : 300;
                    const centerY = bounds ? Math.round((bounds.height / 2 - y) / zoom) : 300;

                    // 1. Cria o node
                    const newNode = await createNode(Number(funnelId), {
                        node_type: copiedNode.node_type,
                        node_name: `${copiedNode.node_name} (cópia)`,
                        position_x: centerX,
                        position_y: centerY,
                        config: copiedNode.config || {},
                    });

                    // 2. Atualiza com config completo (message, title, media, etc)
                    const config = copiedNode.config || {};
                    if (Object.keys(config).length > 0) {
                        await updateNode(newNode.id, { config });
                    }

                    // 3. Cria botoes se houver
                    if (copiedNode.buttons?.length) {
                        for (const btn of copiedNode.buttons) {
                            await createButton(newNode.id, {
                                button_text: btn.button_text,
                                button_type: btn.button_type,
                                button_order: btn.button_order,
                                url: btn.url,
                            });
                        }
                    }

                    // 4. Refetch
                    queryClient.invalidateQueries({ queryKey: ['funnel-details', funnelId] });
                    toast({ title: 'Node colado!' });
                } catch (error: any) {
                    toast({
                        variant: 'destructive',
                        title: 'Erro ao colar node',
                        description: error.response?.data?.detail || 'Ocorreu um erro.',
                    });
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [funnelId, reactFlowInstance, queryClient, toast]);

    if (isLoading) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0f172a]"></div>
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex-shrink-0">
                <Toolbar
                    funnelName={builderMode === 'comment_auto_reply' ? (ruleData?.name || 'Auto-Reply') : (funnelData?.nome || 'Funil')}
                    onBack={onBack || (() => navigate('/meta/flow'))}
                    onValidate={handleValidate}
                    onPreview={() => setShowPreview(true)}
                    onDetails={builderMode === 'comment_auto_reply' ? undefined : () => { setSelectedNode(null); setShowDetails((v) => !v); }}
                    funnel={funnelData}
                    onRename={builderMode === 'comment_auto_reply' && ruleData ? async (name) => {
                        await updateCommentAutoReply(ruleData.id, { name });
                        toast({ title: 'Nome atualizado!' });
                    } : undefined}
                />
            </div>

            {/* Canvas Container */}
            <div className="flex-1 flex min-h-0 overflow-hidden">
            <FunnelStatsProvider value={{
                active: showDetails && !!funnelStats.details,
                byNode: statsByNode,
                focusedId: focusedNodeId,
                funnelId: funnelId ? Number(funnelId) : null,
                start: funnelStats.start,
                end: funnelStats.end,
                pageId: statsPageFilter,
                onClearFocus: () => setFocusedNodeId(null),
                onGoToNode: focusNode,
            }}>
            <div className="flex-1 relative overflow-hidden">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onEdgesDelete={onEdgesDelete}
                    onNodeDragStop={onNodeDragStop}
                    onNodeClick={onNodeClick}
                    onPaneClick={() => setSelectedNode(null)}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    connectionMode={ConnectionMode.Loose}
                    connectionRadius={150}
                    fitView
                    fitViewOptions={{
                        padding: 0.2,
                        minZoom: 0.5,
                        maxZoom: 1.5,
                    }}
                    minZoom={0.1}
                    maxZoom={2}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                    className="bg-[#f8f9fb]"
                    defaultEdgeOptions={{
                        style: { stroke: '#2196F3', strokeWidth: 2 },
                        animated: false,
                        zIndex: 999
                    }}
                >
                    <Background
                        color="#d1d5db"
                        gap={20}
                        size={1.5}
                        style={{ backgroundColor: '#f8f9fb' }}
                    />
                    <Controls className="[&>button]:bg-white [&>button]:text-gray-600 [&>button]:border [&>button]:border-gray-200 [&>button]:shadow-sm [&>button]:hover:bg-gray-50" />
                </ReactFlow>

                {/* Sidebar Flutuante */}
                <Sidebar
                    onAddNode={handleAddNode}
                    categories={builderMode === 'comment_auto_reply' ? commentAutoReplyNodeCategories : undefined}
                />

                {/* Node Panel */}
                {selectedNode && !showDetails && (
                    <NodePanel
                        node={selectedNode}
                        onClose={() => setSelectedNode(null)}
                        page_id={funnelData?.page_id || funnelData?.pages?.[0]?.page_id || ''}
                        builderMode={builderMode}
                        ruleData={ruleData}
                        funnelPageIds={
                            builderMode === 'comment_auto_reply' && ruleData?.page_ids?.length
                                ? ruleData.page_ids
                                : funnelData?.pages?.map(p => p.page_id) || []
                        }
                    />
                )}

                <FlowPreviewModal
                    open={showPreview}
                    onClose={() => setShowPreview(false)}
                    nodes={(funnelData?.nodes as any[]) || []}
                    transitions={funnelData?.transitions || []}
                    pageName={funnelData?.nome}
                />

                <FunnelValidationDialog
                    open={showValidation}
                    onOpenChange={setShowValidation}
                    result={validationResult}
                    onRevalidate={handleValidate}
                />

            </div>
            </FunnelStatsProvider>

            {showDetails && (
                <FunnelStatsPanel
                    details={funnelStats.details}
                    pages={funnelStats.pages}
                    loading={funnelStats.loading}
                    error={funnelStats.error}
                    range={statsRange}
                    onRangeChange={setStatsRange}
                    pageFilter={statsPageFilter}
                    onPageFilter={setStatsPageFilter}
                    focusedId={focusedNodeId}
                    onFocusNode={focusNode}
                    onClose={() => setShowDetails(false)}
                />
            )}
            </div>
        </div>
    );
}

// Componente EXTERNO (wrapper com Provider)
export default function FlowBuilder(props: FlowBuilderProps) {
    return (
        <ReactFlowProvider>
            <FlowBuilderInner {...props} />
        </ReactFlowProvider>
    );
}